import React, { Fragment, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Dialog, DialogContent, DialogActions, Box } from '@mui/material';
import { Typography, colors, Button, Input, IconGraphy } from '@leapeasy/ui-kit';
import ClaimDatePickerInput from 'components/ClaimForm/ClaimDatePickerInput';
import {
  setDataAction,
  getTenantsAction,
  checkPolicyDuplicatedAction,
  setPolicyDuplicatedAction,
  setErrorAction,
} from 'store/actions/claimFormActions';
import { lookupPolicy } from 'store/sagas/APIs/claim';
import { getApiErrorMessage } from 'utils/apiError';
import { formatIsoDateForPicker } from 'components/ClaimForm/helpers';
import {
  cancellationDisclaimerFieldsFromPolicy,
  clearedCancellationDisclaimerFields,
} from 'utils/cancellationDisclaimer';

const resolvePolicyDates = (policy) => {
  const policyStartDate = policy.CoverageStartDate__c || policy.Lease_Start_Date__c;
  const policyEndDate = policy.CoverageEndDate__c || policy.Lease_End_Date__c;
  return { policyStartDate, policyEndDate };
};

const Form1PolicyLookup = (props) => {
  const { actions, formData, handleNext } = props;

  const [riderNumberInput, setRiderNumberInput] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [policyFound, setPolicyFound] = useState(false);
  const [summary, setSummary] = useState(null);
  const [policyId, setPolicyId] = useState(null);
  const [policyStartDate, setPolicyStartDate] = useState('');
  const [policyEndDate, setPolicyEndDate] = useState('');
  const [futurePolicy, setFuturePolicy] = useState(false);
  const [leaseStartDate, setLeaseStartDate] = useState(null);
  const [leaseEndDate, setLeaseEndDate] = useState(null);
  const [duplicated, setDuplicated] = useState(null);

  useEffect(() => {
    if (!formData?.data) {
      return;
    }
    if (formData.data.riderName) {
      setRiderNumberInput(formData.data.riderName);
    }
    if (formData.data.policy) {
      setPolicyId(formData.data.policy);
      setPolicyFound(true);
      setSummary({
        riderName: formData.data.riderName,
        state: formData.data.state,
        landlordName: formData.data.landlordName,
        propertyName: formData.data.propertyName,
      });
    }
    setLeaseStartDate(formatIsoDateForPicker(formData.data.leaseStartDate));
    setLeaseEndDate(formatIsoDateForPicker(formData.data.leaseEndDate));
    const pStart = formData.data.policyStartDate;
    const pEnd = formData.data.policyEndDate;
    setPolicyStartDate(formatIsoDateForPicker(pStart));
    setPolicyEndDate(formatIsoDateForPicker(pEnd));
    if (formData.policyDuplicated?.length > 0) {
      setDuplicated(formData.policyDuplicated[0]);
    } else {
      setDuplicated(null);
    }
  }, [formData]);

  const handleFindPolicy = async () => {
    const trimmed = riderNumberInput.trim();
    if (!trimmed) {
      return;
    }

    setLookupLoading(true);
    setPolicyFound(false);
    setSummary(null);
    setPolicyId(null);
    actions.setPolicyDuplicatedAction(null);
    actions.setDataAction({
      ...formData.data,
      policy: null,
      ...clearedCancellationDisclaimerFields(),
    });

    try {
      const policy = await lookupPolicy(trimmed);
      const { policyStartDate: start, policyEndDate: end } = resolvePolicyDates(policy);

      setPolicyFound(true);
      setPolicyId(policy.policyId);
      setSummary({
        riderName: policy.riderName,
        state: policy.state,
        landlordName: policy.landlordName,
        propertyName: policy.propertyName,
      });
      setPolicyStartDate(formatIsoDateForPicker(start));
      setPolicyEndDate(formatIsoDateForPicker(end));
      setFuturePolicy(
        policy.CoverageStartDate__c && new Date(policy.CoverageStartDate__c) > new Date(),
      );

      actions.setDataAction({
        ...formData.data,
        policy: policy.policyId,
        riderName: policy.riderName,
        state: policy.state,
        landlordName: policy.landlordName,
        propertyName: policy.propertyName,
        policyStartDate: start,
        policyEndDate: end,
        chosenTenant: null,
        landlord: null,
        apartment: null,
        ...cancellationDisclaimerFieldsFromPolicy(policy),
      });
      actions.checkPolicyDuplicatedAction(policy.policyId);
      actions.getTenantsAction(policy.policyId);
    } catch (error) {
      actions.setErrorAction(
        getApiErrorMessage(
          error,
          'No matching policy found. Please check your policy number and try again.',
        ),
      );
    } finally {
      setLookupLoading(false);
    }
  };

  const handleNextButton = () => {
    actions.setDataAction({
      ...formData.data,
      leaseStartDate,
      leaseEndDate,
    });
    handleNext();
  };

  const canProceed =
    policyFound &&
    policyId &&
    leaseStartDate &&
    leaseEndDate &&
    formData.policyDuplicated &&
    formData.policyDuplicated.length === 0 &&
    !(policyStartDate && new Date(policyStartDate) > new Date());

  return (
    <Fragment>
      <Box display="flex" flexDirection="column" gap="12px">
        <Input
          label="Policy Number"
          value={riderNumberInput}
          onChange={(e) => setRiderNumberInput(e.target.value)}
          type="text"
          minWidth="100%"
          fullWidth
          size="medium"
          showCopy={false}
          icon={<IconGraphy icon="EditorLayout.Numbers" />}
          placeholder="e.g. LEAP-3179679"
        />

        <Button
          size="medium"
          variant="primary"
          onClick={handleFindPolicy}
          disabled={!riderNumberInput.trim() || lookupLoading}
          style={{ alignSelf: 'flex-start' }}
        >
          {lookupLoading ? 'Finding policy…' : 'Find policy'}
        </Button>

        {policyFound && summary && (
          <Box
            sx={{
              padding: '24px',
              gap: '8px',
              background: 'rgba(249, 248, 249, 0.8)',
              border: '1px solid #F8F4F9',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="body1" style={{ color: colors.purple[900], fontWeight: 600 }}>
              Policy found
            </Typography>
            <Typography variant="body2" style={{ color: colors.neutral[900] }}>
              Policy: {summary.riderName}
            </Typography>
            <Typography variant="body2" style={{ color: colors.neutral[900] }}>
              State: {summary.state}
            </Typography>
            <Typography variant="body2" style={{ color: colors.neutral[900] }}>
              Operator: {summary.landlordName || '—'}
            </Typography>
            <Typography variant="body2" style={{ color: colors.neutral[900] }}>
              Property: {summary.propertyName || '—'}
            </Typography>
          </Box>
        )}

        <Box display="flex" gap={2}>
          <Box sx={{ flexGrow: 1, width: 0 }}>
            <ClaimDatePickerInput
              label="Policy Start Date"
              value={policyStartDate || ''}
              minDate="01/01/2010"
              size="medium"
              showActionButtons={false}
              maxWidth="100%"
              disabled
            />
          </Box>
          <Box sx={{ flexGrow: 1, width: 0 }}>
            <ClaimDatePickerInput
              label="Policy End Date"
              value={policyEndDate || ''}
              size="medium"
              minDate="01/01/2010"
              showActionButtons={false}
              maxWidth="100%"
              disabled
            />
          </Box>
        </Box>

        <Box display="flex" gap={2}>
          <Box sx={{ flexGrow: 1, width: 0, position: 'relative' }}>
            <ClaimDatePickerInput
              label="Lease Start Date"
              value={leaseStartDate || ''}
              minWidth="100%"
              minDate="01/01/2018"
              size="medium"
              disabled={!policyFound}
              maxWidth="100%"
              onChange={(e) => setLeaseStartDate(e.target.value)}
            />
          </Box>
          <Box sx={{ flexGrow: 1, width: 0, position: 'relative' }}>
            <ClaimDatePickerInput
              label="Lease End Date"
              value={leaseEndDate || ''}
              minWidth="100%"
              minDate="01/01/2018"
              size="medium"
              disabled={!policyFound}
              maxWidth="100%"
              onChange={(e) => setLeaseEndDate(e.target.value)}
            />
          </Box>
        </Box>
      </Box>

      {formData.policyDuplicated && formData.policyDuplicated.length === 0 && futurePolicy && (
        <Dialog open={futurePolicy} maxWidth="md">
          <DialogContent>
            <Typography
              variant="body1"
              style={{ fontWeight: '600', textAlign: 'center', color: colors.purple[900] }}
            >
              The policy period you have selected is a future policy that has not started yet.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="primary" onClick={() => setFuturePolicy(false)}>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {duplicated && (
        <Dialog open={!!duplicated} maxWidth="md">
          <DialogContent>
            <Typography variant="body1" style={{ textAlign: 'center', color: colors.purple[900] }}>
              A claim already exists for this policy.
            </Typography>
            <Typography variant="body1" style={{ textAlign: 'center', marginTop: '8px' }}>
              Claim # {duplicated.Name}
            </Typography>
            <Typography variant="body2" style={{ textAlign: 'center', marginTop: '8px' }}>
              Created Date {new Date(duplicated.CreatedDate).toISOString().slice(0, 10)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="primary" onClick={() => setDuplicated(null)}>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <Button onClick={handleNextButton} variant="primary" size="medium" disabled={!canProceed}>
          <Typography variant="body1">Next</Typography>
        </Button>
      </Box>
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  formData: state.getIn(['formData']),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      setDataAction,
      getTenantsAction,
      checkPolicyDuplicatedAction,
      setPolicyDuplicatedAction,
      setErrorAction,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Form1PolicyLookup);
